import React from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import Button from '../../../incase-domain/components/button/Button';
import i18n from '../../../i18n';
import {PresentationPrototypeType} from '../../../incase-domain/presentationPrototype';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import TextAreaWithCharacterCounter from '../../../components/slides/highlighted/common/TextAreaWithCharacterCounter';

type PresentationPrototypeTypeLabel = 'medical' | 'business' | 'clearType';

const presentationPrototypeTypes: Array<{
    label: PresentationPrototypeTypeLabel;
    value: PresentationPrototypeType;
    icon: string;
}> = [{label: 'medical', value: PresentationPrototypeType.MEDICAL_CASE, icon: 'solar:heart-pulse-linear'}];

const promptTypeTranslationKey = (label: PresentationPrototypeTypeLabel): `generator.promptTypes.${PresentationPrototypeTypeLabel}` =>
    `generator.promptTypes.${label}` as const;

export interface GeneratorPromptInputProps {
    prompt: string;
    onPromptChange: (value: string) => void;
    onSubmit: () => void;
    hasPrototype: boolean;
    type: PresentationPrototypeType | undefined;
    onTypeChange: (type: PresentationPrototypeType | undefined) => void;
    isLoading: boolean;
    error?: string;
}

function GeneratorPromptInput({
    prompt,
    onPromptChange,
    onSubmit,
    hasPrototype,
    type,
    onTypeChange,
    isLoading,
    error,
}: GeneratorPromptInputProps): JSX.Element {
    const {t} = useTranslation();

    const presentationType = presentationPrototypeTypes.find((pt) => pt.value === type);

    return (
        <div className="c-generatorPage__inputArea">
            <TextAreaWithCharacterCounter
                placeholder={i18n.t(hasPrototype ? 'generator.promptModifyPlaceholder' : 'generator.promptPlaceholder')}
                value={prompt}
                onChange={onPromptChange}
                disabled={isLoading}
            />

            <div className="c-generatorPage__inputArea_footer">
                <div className="u-flex u-gap-1">
                    {!hasPrototype && (
                        <DropdownMenuCompact
                            items={[
                                [
                                    {
                                        label: t('generator.promptTypes.clearType'),
                                        icon: 'ph:x',
                                        onClick: () => onTypeChange(undefined),
                                        active: type === undefined,
                                    },
                                ],
                                presentationPrototypeTypes.map((pt) => ({
                                    label: t(promptTypeTranslationKey(pt.label)),
                                    icon: pt.icon,
                                    onClick: () => onTypeChange(pt.value),
                                    active: type === pt.value,
                                })),
                            ]}
                            width="full"
                            menuPosition={DropdownMenuCompactMenuPosition.UP}
                        >
                            <Button size="xs" variant="ghost" leading="ph:plus" disabled={hasPrototype || isLoading}>
                                {t('generator.addPromptType')}
                            </Button>
                        </DropdownMenuCompact>
                    )}

                    {presentationType && (
                        <Button
                            size="xs"
                            variant="ghost"
                            color="secondary"
                            className="c-generatorPage__typeChip"
                            onClick={() => onTypeChange(undefined)}
                            disabled={hasPrototype || isLoading}
                        >
                            <span className="c-generatorPage__typeChip_icon">
                                <Icon className="c-generatorPage__typeChip_iconDefault" icon={presentationType.icon} />
                                <Icon className="c-generatorPage__typeChip_iconHover" icon="ph:x" />
                            </span>
                            {t(promptTypeTranslationKey(presentationType.label))}
                        </Button>
                    )}
                </div>

                <Button
                    size="sm"
                    variant="solid"
                    color="secondary"
                    trailing="solar:plain-3-linear"
                    onClick={onSubmit}
                    disabled={!prompt}
                    loading={isLoading}
                >
                    {t(hasPrototype ? 'generator.modify' : 'generator.generate')}
                </Button>
            </div>

            {error && <div className="u-text-red u-mb-1">{error}</div>}
        </div>
    );
}

export default GeneratorPromptInput;
